import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, KeyRound, XCircle, Eye, EyeOff } from 'lucide-react';
import { formatApiError, extractApiErrorCode } from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Public — choose a new password with a single-use token from the URL.
// On success the client is bounced to /login with a flash toast; there
// is no session in flight here.
export default function ResetPasswordPage() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = (params.get('token') || '').trim();

    // We pre-check the token server-side on mount. If it fails, the
    // form NEVER renders — the user sees the dead-link page and cannot
    // even attempt a password change, even if the token was consumed
    // seconds earlier in another tab.
    const [phase, setPhase] = useState(token ? 'loading' : 'dead');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                await axios.get(`${API}/api/auth/reset-password/${encodeURIComponent(token)}`);
                if (!cancelled) setPhase('ready');
            } catch (_) {
                if (!cancelled) setPhase('dead');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const passwordValid = useMemo(() => password.length >= 12, [password]);
    const passwordsMatch = useMemo(() => password === confirm && confirm.length > 0, [password, confirm]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!passwordValid || !passwordsMatch || submitting || !token) return;
        setSubmitting(true);
        setError('');
        try {
            await axios.post(`${API}/api/auth/reset-password`, {
                token, new_password: password,
            });
            toast.success(t('reset_password.success_toast'));
            navigate('/login', { replace: true, state: { flash: 'password_reset' } });
        } catch (err) {
            const code = extractApiErrorCode(err);
            const status = err.response?.status;
            const localized = code
                ? t(`auth.errors.${code}`, { defaultValue: formatApiError(err) })
                : formatApiError(err);
            // If the server said the token is dead (404), flip the whole
            // page to the dead state — no point letting the user retry.
            if (status === 404) {
                setPhase('dead');
                setError(localized);
            } else {
                setError(localized);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (phase === 'loading') {
        return (
            <Shell>
                <div className="w-16 h-16 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-2 font-['Outfit']">
                    {t('reset_password.checking_title')}
                </h1>
            </Shell>
        );
    }

    if (phase === 'dead') {
        return (
            <Shell testId="reset-password-dead">
                <div className="w-16 h-16 rounded-full bg-[#FEE2E2] flex items-center justify-center mx-auto mb-6">
                    <XCircle className="w-9 h-9 text-[#DC2626]" />
                </div>
                <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-3 font-['Outfit']">
                    {t('reset_password.dead_title')}
                </h1>
                <p className="text-center text-[#4B5563] mb-6">
                    {error || t('auth.errors.invalid_or_expired_token')}
                </p>
                <Link to="/forgot-password" data-testid="reset-password-request-new">
                    <Button variant="outline" className="w-full">{t('reset_password.request_new')}</Button>
                </Link>
            </Shell>
        );
    }

    return (
        <Shell testId="reset-password-form">
            <div className="w-16 h-16 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-6">
                <KeyRound className="w-8 h-8 text-[#002FA7]" />
            </div>
            <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-2 font-['Outfit']">
                {t('reset_password.title')}
            </h1>
            <p className="text-center text-[#4B5563] mb-6">
                {t('reset_password.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <Label htmlFor="reset-password">{t('reset_password.password_label')}</Label>
                    <div className="relative">
                        <Input
                            id="reset-password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={12}
                            autoComplete="new-password"
                            className="pr-10"
                            data-testid="reset-password-input"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    <p className={`mt-1 text-xs ${passwordValid ? 'text-[#059669]' : 'text-[#6B7280]'}`}>
                        {t('reset_password.password_hint')}
                    </p>
                </div>
                <div>
                    <Label htmlFor="reset-confirm">{t('reset_password.confirm_label')}</Label>
                    <div className="relative">
                        <Input
                            id="reset-confirm"
                            type={showConfirm ? 'text' : 'password'}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            autoComplete="new-password"
                            className="pr-10"
                            data-testid="reset-password-confirm-input"
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                        >
                            {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    {confirm && !passwordsMatch && (
                        <p className="mt-1 text-xs text-[#DC2626]" data-testid="reset-password-mismatch">
                            {t('reset_password.mismatch')}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="p-3 rounded-lg bg-[#FEE2E2] border border-[#EF4444] text-sm text-[#B91C1C]" data-testid="reset-password-error">
                        {error}
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={!passwordValid || !passwordsMatch || submitting}
                    className="w-full bg-[#002FA7] hover:bg-[#001F70]"
                    data-testid="reset-password-submit-btn"
                >
                    {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('reset_password.submitting')}</>
                    ) : t('reset_password.submit')}
                </Button>
            </form>
        </Shell>
    );
}

function Shell({ children, testId }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4 py-12" data-testid={testId || 'reset-password-page'}>
            <div className="max-w-md w-full bg-white p-8 rounded-2xl border border-[#E5E7EB] shadow-sm">
                {children}
            </div>
        </div>
    );
}
